import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { Clipboard } from '@capacitor/clipboard';
import {
  ModalController,
  NavController,
  RefresherCustomEvent
} from '@ionic/angular';
import { OrdersService } from 'src/app/services/order/orders.service';
import { Subject } from 'rxjs/internal/Subject';
import { firstValueFrom, takeUntil } from 'rxjs';
import { RtsDay, RtsVehicle } from 'src/app/dto/get-rts-result.dto';
import { ForwarderReportGeneratorService } from 'src/app/services/docs-service/forwarder-report.service';
import { OrdersMergeService } from 'src/app/services/order/orders-merge.service';
import { ErrorToastService } from 'src/app/services/error-toast.service';
import { HeaderDto } from '../../dto/header.dto';
import { Order } from '../../dto/order.dto';
import { Status } from '../../dto/status.enum';
import { ActionsModalComponent } from '../../shared/actions-modal/actions-modal.component';
import {
  ARCHIVED_REQUESTS,
  REQUESTS_IN_WORK
} from '../../utils/constants/router.constants';
import { OrderChangesService } from '../../services/order/order-changes.service';
import { UploadDocumentsService } from '../../services/docs-service/upload-documents.service';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-days-list',
  templateUrl: './days-list.component.html',
  styleUrls: ['./days-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Экран заявки: список дней отгрузки с суммарными весами, завершение заявки, скачивание отчёта
 * экспедитора, загрузка финальных документов. Данные берутся из реактивного потока заказа: при первой
 * подписке, если состояния ещё нет — грузим с сервера и сливаем ({@link #handleOrderFetch}), иначе
 * обновляем экран из потока ({@link #handleOrderUpdate}). Pull-to-refresh пере-сливает текущую версию с
 * сервером ({@link #updatePageData}). Стратегия OnPush: перерисовка — по {@code markForCheck}.
 */
export class DaysListComponent implements OnInit, OnDestroy {
  showEndOrderBlock: boolean = false;
  orderID: number;
  headerDataValues: HeaderDto;
  daysList: RtsDay[];
  loading: boolean;
  loadingSpinner: boolean = false;
  request: Order;
  phoneHref: string;
  isToastOpen = false;
  isDownloadingReport = false;
  targetUrl = window.location.pathname.split('/')[1];
  isRefresherDisabled: boolean;
  defaultBackUrl: string;
  isUpdatingAfterUpload: boolean = false;
  // Явный сигнал обновления полки документов: инкрементируем при каждом обновлении данных. Нужен для
  // случая фоновой загрузки документов (isUpdatingAfterUpload=true), когда loading НЕ тогглится и полка
  // под *ngIf="!loading" не пересоздаётся — иначе счётчик документов/состояние поручения остались бы
  // устаревшими до полной перезагрузки.
  shelfRefreshSignal = 0;

  monthNames: Record<number, string> = {
    1: 'Январь',
    2: 'Февраль',
    3: 'Март',
    4: 'Апрель',
    5: 'Май',
    6: 'Июнь',
    7: 'Июль',
    8: 'Август',
    9: 'Сентябрь',
    10: 'Октябрь',
    11: 'Ноябрь',
    12: 'Декабрь'
  };

  private destroy$ = new Subject();
  private updateCount = 0;

  constructor(
    private navCtrl: NavController,
    private router: Router,
    public cd: ChangeDetectorRef,
    private ordersService: OrdersService,
    private orderChangesService: OrderChangesService,
    private forwarderReportGeneratorService: ForwarderReportGeneratorService,
    private ordersMergeService: OrdersMergeService,
    private modalCtrl: ModalController,
    private errorToastService: ErrorToastService,
    private uploadDocumentsService: UploadDocumentsService
  ) {}

  /**
   * Перевозчик по заявке ещё не назначен: она опубликована на рынке перевозок, но её никто не взял.
   * Реестра при этом нет по естественной причине, а не из-за сбоя загрузки, — и говорить надо об ожидании.
   */
  get carrierNotAssigned(): boolean {
    return this.rtsStateVisible && this.request.carrierAssigned === false && this.noVehicles;
  }

  /** Перевозчик назначен, но машин ещё не подавал: дни есть или пусты, а рейсов в них нет. */
  get noVehiclesYet(): boolean {
    return (
      this.rtsStateVisible && this.request.carrierAssigned === true && this.noVehicles
    );
  }

  /**
   * Реестр не загрузился. Отличаем от двух состояний выше: о них известно от сервера, а сюда попадаем,
   * когда сведений о перевозчике нет вовсе — ответ не пришёл (в том числе офлайн).
   */
  get rtsUnavailable(): boolean {
    return (
      // == null: сведений нет и когда поле отсутствует, и когда пришло как null.
      this.rtsStateVisible && this.request.carrierAssigned == null && this.noVehicles
    );
  }

  /** Показывать сам список дней: пока машин нет, вместо него стоит объяснение состояния. */
  get daysVisible(): boolean {
    return !this.loading && !this.noVehicles;
  }

  /**
   * В реестре нет ни одной машины. Дни при этом могут быть: сервер строит скелет по датам отгрузки, и
   * такой список сам по себе ничего не сообщает — до подачи машин показываем вместо него состояние.
   */
  private get noVehicles(): boolean {
    return !this.daysList?.some(day => day.vehicles?.length);
  }

  /**
   * Состояние реестра вообще показывается. Открытую заявку экспедитор ещё не принял — говорить ей о
   * перевозчике и машинах нечего; то же ограничение стояло у прежнего единственного сообщения.
   */
  private get rtsStateVisible(): boolean {
    return !this.loading && !!this.request && this.request.status !== Status.OPEN;
  }

  ngOnInit(): void {
    this.defaultBackUrl = window.location.pathname
      .split('/')
      .slice(0, -2)
      .join('/');

    this.loading = true;
    this.isRefresherDisabled = true;

    this.orderID =
      +window.location.pathname.split('/')[
        window.location.pathname.split('/').findIndex(el => el === 'days') + 1
      ];

    this.headerDataValues = {
      title: `Заявка №${this.orderID}`
    };

    this.orderChangesService
      .getOrderSubject(this.targetUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async orderUpdate => {
        this.updateCount += 1;
        this.loading = true;
        // Поток заказа ведёт экран: нет состояния — грузим с сервера, есть — обновляем из потока.
        // updateCount различает первую эмиссию (нужно однократное слияние) от последующих.
        if (!orderUpdate) {
          await this.handleOrderFetch();
        } else {
          await this.handleOrderUpdate(orderUpdate);
        }

        this.loading = false;
        this.isRefresherDisabled = false;
        this.cd.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  pageInitActions(r: Order) {
    this.request = r;
    this.daysList = this.request?.registry?.days;

    if (r.registry) {
      this.showEndOrderBlock = this.request.status === Status.IN_WORK;
    }

    this.phoneHref = `tel:${this.request.seller.phone}`;
    this.cd.markForCheck();
  }

  async copyText(text: string) {
    await Clipboard.write({
      string: text
    });
    this.toastSetStatus(true);
  }

  toastSetStatus(isOpen: boolean) {
    this.isToastOpen = isOpen;
    this.cd.markForCheck();
  }

  async openDayDetails(dayId: string) {
    // Детализация дня доступна только для заявок в работе и в архиве. Для статуса OPEN список дней не
    // показывается (используется OpenRequestInfoComponent), и маршрута детализации дня под OPEN нет —
    // не строим заведомо недостижимый URL.
    if (this.request.status === Status.OPEN) {
      return;
    }

    // День-скелет приходит без номера строки РТС: открывать нечего, а обращение к пустому id падало бы.
    // Молча ничего не делать нельзя — нажатие без отклика читается как поломка, поэтому объясняем.
    if (!dayId) {
      await this.showWaitingForVehicles(false);
      return;
    }

    const id = dayId.replace(/\|/g, '');
    const day = this.request.getDayById(id);
    let path: string;

    switch (this.request.status) {
      case Status.COMPLETED:
      case Status.CLOSED:
        path = ARCHIVED_REQUESTS;
        break;
      default:
        path = REQUESTS_IN_WORK;
        break;
    }

    if (day?.vehicles.length) {
      this.navCtrl.navigateForward([`${path}/days/${this.orderID}/day/${id}`]);
      return;
    }

    if (await this.showWaitingForVehicles(true)) {
      this.navCtrl.navigateForward(
        `${path}/days/${this.orderID}/day/${id}/add-new-car`
      );
    }
  }

  async showEndOrderWarning(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          confirmButton: true,
          confirmButtonText: 'Завершить заявку',
          message:
            'Вы действительно хотите завершить заявку на экспедирование? Обратите внимание, что не все дни отгрузки были завершены.',
          title: 'Завершение заявки',
          imageSrc: '../../../assets/images/warn-end-order.svg',
          errorTitle: true
        }
      }
    });
    await modal.present();
    const { role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      await this.navCtrl.navigateForward([
        `requests-in-work/days/${this.orderID}/end-order`
      ]);
    }
  }

  async showEndOrderError(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          message:
            'Завершите хотя бы один день погрузки, для завершения заявки.',
          title: 'Завершение заявки невозможно',
          imageSrc: '../../../assets/images/warn-end-order.svg',
          errorTitle: true,
          closeButton: true
        }
      }
    });
    await modal.present();
  }

  /**
   * Завершение заявки: без хотя бы одного завершённого дня — нельзя (окно ошибки); если завершены не все
   * дни — предупреждение; иначе сразу переход к экрану завершения.
   */
  async endOrder() {
    if (!this.request.checkCompletedDayExist()) {
      await this.showEndOrderError();
    } else if (!this.request.checkCompletedStatus()) {
      await this.showEndOrderWarning();
    } else {
      await this.navCtrl.navigateForward([
        `requests-in-work/days/${this.orderID}/end-order`
      ]);
    }
  }

  async downloadForwarderReport() {
    if (this.isDownloadingReport) {
      return;
    }

    this.isDownloadingReport = true;
    this.cd.markForCheck();
    setTimeout(async () => {
      try {
        await this.forwarderReportGeneratorService.getForwarderReport(
          this.request
        );
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        this.isDownloadingReport = false;
        this.cd.markForCheck();
      }
    }, 100);
  }

  calcAssignedWeight() {
    if (this.request.registry) {
      return this.request.getTotalAssignedWeight();
    }
    return 0;
  }

  calcLoadedWeight(item: RtsDay): string {
    const totalKg = item.vehicles.reduce(
      (acc: number, vehicle: RtsVehicle) =>
        acc + (vehicle.loadingData?.netWeight || 0),
      0
    );
    return totalKg.toLocaleString('ru-RU');
  }

  /**
   * Обновление данных (pull-to-refresh и после загрузки документов): берём текущую версию из потока,
   * пере-сливаем с сервером ({@code mergeOrder}) и публикуем. Флаг {@code isUpdatingAfterUpload} гасит
   * крупный индикатор загрузки, когда обновление идёт фоном после выгрузки документа.
   */
  async updatePageData() {
    if (!this.isUpdatingAfterUpload) {
      this.loading = true;
    }
    this.isRefresherDisabled = true;
    const currentOrder = this.orderChangesService
      .getOrderSubject(this.targetUrl)
      .getValue();
    if (currentOrder) {
      const newOrder = await this.ordersMergeService.mergeOrder(currentOrder);
      this.orderChangesService.updateRequests(newOrder, this.targetUrl);
    }
    // Явно обновляем полку документов (счётчик + состояние поручения): при фоновой загрузке документов
    // loading не тогглился и полка не пересоздавалась бы.
    this.shelfRefreshSignal += 1;
    this.loading = false;
    this.isRefresherDisabled = false;
    this.isUpdatingAfterUpload = false;
  }

  async handleRefresh(event: RefresherCustomEvent) {
    this.updateCount = 1;
    this.isRefresherDisabled = true;
    await this.updatePageData();
    void event.target.complete();
  }

  openUploadDocuments(): void {
    this.uploadDocumentsService
      .uploadDocumentsFlow(this.request.bargainId, 'FORWARDER_FINAL_DOCS')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          void this.errorToastService.showSuccess('Файл успешно загружен');
          this.isUpdatingAfterUpload = true;
          void this.updatePageData();
        },
        error: err => {
          Sentry.captureException(err);
          void this.errorToastService.showError('Ошибка при загрузке файла');
          this.isUpdatingAfterUpload = true;
          void this.updatePageData();
        }
      });
  }

  /**
   * Первичная загрузка заказа с сервера (когда в потоке ещё пусто). Для активных заявок (не {@code OPEN})
   * сливает серверную версию с локальными правками ({@code mergeOrder}) и кладёт в поток; для {@code OPEN}
   * — кладёт серверную как есть (реестра дней там нет). Нет ответа — уводим назад с ошибкой.
   */
  private async handleOrderFetch() {
    const orderResponse = await firstValueFrom(
      this.ordersService.getOrderById(this.orderID)
    );

    if (!orderResponse) {
      this.errorToastService.acceptOrderErrorModal().then();
      void this.router.navigate(['../']);
      return;
    }

    if (orderResponse.success) {
      const { data } = orderResponse;
      this.pageInitActions(data);
      if (data.status !== 'OPEN') {
        const newOrder = await this.ordersMergeService.mergeOrder(data);
        this.orderChangesService.updateRequests(newOrder, this.targetUrl);
      } else {
        this.orderChangesService.setOrder(data, this.targetUrl);
      }
    }
    this.cd.markForCheck();
  }

  /**
   * Обновление экрана из потока (состояние уже есть). Слияние с сервером выполняем ТОЛЬКО на ПЕРВОЙ
   * эмиссии ({@code updateCount === 1}) и только для не-{@code OPEN} — чтобы подтянуть серверные изменения
   * один раз при входе, а последующие локальные правки не гоняли слияние на каждой эмиссии.
   */
  private async handleOrderUpdate(orderUpdate: Order) {
    this.pageInitActions(orderUpdate);

    if (!orderUpdate.status.includes(Status.OPEN) && this.updateCount === 1) {
      const newOrder = await this.ordersMergeService.mergeOrder(orderUpdate);
      this.orderChangesService.updateRequests(newOrder, this.targetUrl);
    }
    this.cd.markForCheck();
  }

  /**
   * Окно «Поиск машин»: машин на этот день перевозчик ещё не подал. Предложение завести реестр вручную
   * ({@code withLocalRts}) уместно только у дня с номером строки РТС — у дня-скелета его нет, и заводить
   * машины было бы некуда.
   *
   * @returns выбрано ли создание реестра вручную
   */
  private async showWaitingForVehicles(withLocalRts: boolean): Promise<boolean> {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          errorTitle: true,
          closeButton: true,
          message:
            'Дождитесь пока логистическая компания предоставит РТС для данного дня, чтобы начать работу',
          title: 'Поиск машин',
          imageSrc: '../../../assets/images/no-rts-modal.svg',
          confirmButton: withLocalRts,
          confirmButtonText: 'Создать РТС локально '
        }
      }
    });
    modal.present();
    const { role } = await modal.onWillDismiss();

    return role === 'confirm';
  }

}
